/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useSearchParams, Link } from "react-router-dom";
import { useGetForums } from "@/services/forumService";
import { useGetForumsGroups } from "@/services/forumGroupService";
import { updateSearchParams } from "@/utils/updateSearchParams";
import { CircleAlert, Filter } from "lucide-react";

import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";

import MultipleSelector from "@/components/ui/multipleSelector";
import SearchableSelector from "@/components/ui/searchableSelector";

/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import "dayjs/locale/fr"

dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)



const initialState = {
    errorMessage: "",
    isLoading: false,
    forums: null,
    groups: null,
};

const ACTIONS = {
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",
    SET_FORUMS: "setForums",
    SET_GROUPS: "setGroups",
};

const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, errorMessage: action.payload };
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload };
        case ACTIONS.SET_FORUMS:
            return { ...state, forums: action.payload };
        case ACTIONS.SET_GROUPS:
            return { ...state, groups: action.payload };

        default:
            console.error("Unknown action.type " + action.type);
            throw new Error();
    }
};


export default function Index() {

    const [, setTitle, , , , setShowTitle] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState);
    const getForums = useGetForums()
    const getForumsGroups = useGetForumsGroups()

    const [searchParams, setSearchParams] = useSearchParams();

    
    useEffect(() => {
        setTitle("Liste des forums")
        setShowTitle(false)

        return () => {
            setTitle("Titre")
            setShowTitle(true)
        }
    }, [setShowTitle, setTitle])



    /* On récupère les groupes disponibles */
    useEffect(() => {
        const getGroups = async () => {
            const groups = await getForumsGroups()
            dispatch({ type: ACTIONS.SET_GROUPS, payload: groups })
        }

        getGroups()

    }, [])


    /* On récupère les forums au changement de filtres */
    useEffect(() => {

        const updateForumList = async () => {

            const filters = {
                group_id: searchParams.getAll("group_id") || null,
                name: searchParams.get("name")
            };

            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const forums = await getForums(filters);
                dispatch({ type: ACTIONS.SET_FORUMS, payload: forums });
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            } catch (error) {
                console.error("admin/forums/index::searchHandler", error);
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            }
        }


        updateForumList()
    }, [searchParams])


    return (
        <>  
            { state?.errorMessage && <div className="bg-red-300 text-red-600 p-4 flex flex-row items-center gap-3 mb-4"><CircleAlert size={24} />{state.errorMessage}</div> }
            <div className="w-full flex flex-col gap-2 items-stretch">
                <div className="flex flex-row items-center gap-2 text-secondary flex-wrap">
                    <Filter size={16} className="text-secondary" />
                    <h2 className="text-primary mr-4" lang="fr">Filtres de forum</h2>
                    <div className="flex flex-row items-center gap-2">
                        <span className="text-sm" lang="fr">Groupe(s)&nbsp;:</span>
                        <MultipleSelector
                            key={state.groups?.length || 'loading'}
                            items={state?.groups || []}
                            selectedItems={Array.isArray(searchParams.getAll("group_id")) ? searchParams.getAll("group_id").map(Number) : []}
                            selectionableItems={state?.forums?.map(f => f.groups.map(g => g.id)).flat()}
                            placeholder="Ajouter groupe"
                            onChange={selectedOptions => setSearchParams(updateSearchParams(searchParams, { group_id: selectedOptions }))}
                            styles={{
                                container: "text-sm",
                                tag: "text-xs",
                                input: "text-xs",
                                dropdown: "text-xs",
                                item: "text-sm",
                            }}
                        />
                    </div>
                    <div className="flex flex-row items-center gap-2 ml-4">
                        <span className="text-sm" lang="fr">Forum&nbsp;:</span>
                        <SearchableSelector
                            key={state.forums?.length || 'loading'}
                            items={state?.forums ? Array.from(new Map(state.forums.map(forum => [forum.name, {name: forum.name}])).values()) : []}
                            selectedItem={searchParams.get("name")}
                            itemIdKey="name"
                            itemNameKey="name"
                            placeholder="Nom du forum"
                            onChange={ selectedOption => setSearchParams(updateSearchParams(searchParams, { name: selectedOption })) }
                            styles={{
                                container: "text-sm px-2 py-1",
                                tag: "text-xs py-1",
                                input: "text-xs py-1 px-2",
                                dropdown: "text-xs",
                                item: "text-sm",
                            }}
                        />
                    </div>
                </div>

                { ( state.isLoading )
                    ? <div className="w-full" lang="fr">En chargement</div>
                    : <div className="w-full flex flex-col gap-2 items-stretch">
                        { state?.forums?.length > 0 
                            ? state?.forums?.filter(forum => !state.forum_name || forum.name.toLowerCase().includes(state.forum_name.toLowerCase())).map((forum, i) => {
                            return (
                                <Link key={i} 
                                    className="w-full flex flex-row justify-between border border-gray-400 p-3 cursor-pointer text-primary hover:bg-gray-100"
                                    to={`/forums/${forum.id}`}
                                >
                                    <div className="flex flex-col gap-1">
                                        <span className="text-lg" lang="fr">{forum.name}</span>
                                        {
                                            forum.topic_count == 0 ?
                                                <span className="text-sm italic text-gray-500">Aucun sujet</span>
                                                : forum.topic_count == 1 ?
                                                    <span className="text-sm">{forum.topic_count} sujet, dernière mise à jour : {dayjs(forum.last_topic_update).fromNow()}</span>    
                                                    : <span className="text-sm">{forum.topic_count} sujets, dernière mise à jour : {dayjs(forum.last_topic_update).fromNow()}</span>
                                        }
                                        <span className="text-sm text-gray-500" lang="fr">{forum.description}</span>
                                    </div>
                                    <div className="flex flex-row items-center gap-2">
                                        {forum.groups?.filter((group) => group.forum_tag).map((group, i) => {
                                            const bgColor = group.color ? `bg-${group.color}-500` : "bg-secondary";
                                            const borderColor = group.color ? `border-${group.color}-500` : "bg-secondary";
                                            const textColor = group.color ? `text-white` : "text-secondary";
                                            return (
                                                <span key={i} className={`text-xs border rounded-md px-2 py-1 ${bgColor} ${borderColor} ${textColor}`} lang="fr">
                                                    {group.name}
                                                </span>
                                            )
                                        })}
                                    </div>

                                </Link>
                            )}) 
                            
                            : "Aucun forum à afficher"
                        }
                    </div>
                } 
            </div>
        
        </>
    )


}